// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import {
  createLightTheme,
  createDarkTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

/**
 * GMS brand ramp — the single source of GMS brand colour across every agent.
 *
 * A 16-stop Fluent `BrandVariants` ramp built around the 4:2:1 GRC primary green
 * #0E8761 at stop 80 (PR-4, JJ 2026-08-13: darkened from #0E8A63 so the white
 * verb label measures 4.51:1 — see docs/Reference/Styling/pending-rulings.md).
 * Fluent's convention: stop 10 is the DARKEST and 160 the
 * LIGHTEST, with 80 the primary — stops 10–70 darken (hover/pressed/foreground
 * states), 90–160 lighten toward white (subtle/selected backgrounds).
 * `FluentProvider` emits all ~104 `--colorBrand*` custom properties from whatever
 * theme it is given, so editing these 16 stops re-skins the brand colour across
 * *every* agent that consumes this file — with zero per-repo App.css colour edits.
 *
 * Mint #05FABD sits deliberately OUTSIDE the ramp as `--brand-accent` (dark-mode
 * accent + brand highlights); it fails contrast (~1.6:1) as text on white and must
 * never become a ramp stop.
 *
 * This is an Ops-owned, byte-identical synced artifact (see `.claude/shared-frontend.json`
 * owns[]) distributed to the spokes by `sync-check.yml`. See ADR-005 — Brand-Token
 * Propagation (Audit Agent `docs/Decisions/ADR-005-Brand-Token-Propagation.md`).
 *
 * Source of truth: `docs/Reference/Rebrand-421/Shared-CSS-Reference-v4-DRAFT.md` (EPIC #5854).
 */
export const gmsBrand: BrandVariants = {
  10: '#04231A',
  20: '#062E22',
  30: '#083D2D',
  40: '#0A4C38',
  50: '#0C5B43',
  60: '#0D6B4F',
  70: '#0E7A59',
  80: '#0E8761',
  90: '#2AA379',
  100: '#4BB890',
  110: '#6EC9A6',
  120: '#8FD8BB',
  130: '#AFE4CD',
  140: '#CBEEDE',
  150: '#E2F7EC',
  160: '#F2FCF7',
};

/**
 * Font stacks — Space Grotesk Bold for headings/numerals, DM Sans for UI.
 *
 * No font files have been delivered by the agency yet, so both stacks run on
 * web-safe fallbacks; when the woff2 files land they are self-hosted (no CDN at
 * runtime) and the first family in each stack starts resolving with no further
 * code change.
 */
export const FONT_UI = "'DM Sans', system-ui, 'Segoe UI', sans-serif";
export const FONT_DISPLAY = "'Space Grotesk', 'DM Sans', system-ui, sans-serif";

/**
 * 4:2:1 brand token catalogue — light.
 *
 * Emitted as CSS custom properties on `:root` by `useResolvedTheme`. They live here
 * rather than in a `.css` file because the styling-consistency guard forbids raw hex
 * in CSS; `*.ts` is the documented home for dynamic colour.
 */
export const brandTokensLight: Readonly<Record<string, string>> = {
  /* Shell — black in BOTH themes, the cross-theme brand constant */
  '--shell': '#0C0F0E',
  '--shell-text': '#F2F7F4',

  /* Surfaces. `--raised` is the third elevation step; it carries a LIGHT value even
     though only dark leans on it, because `useResolvedTheme` applies a catalogue by
     setting each key it holds and never clears the keys it does not — a dark-only
     property would survive the switch back to light as a stale #1F2622 (#5870). The
     two catalogues must therefore declare the same key set. */
  '--bg': '#F7F8F8',
  '--surface': '#FFFFFF',
  '--raised': '#F0F3F1',
  '--line': '#E8ECEA',
  '--hairline': '#E8ECEA',
  '--line-soft': '#F0F3F1',

  /* Text */
  '--text': '#1A211E',
  '--muted': '#66736C',

  /* Brand. PR-4 values (JJ 2026-08-13, WCAG-verified): --deep #0E8761 carries its
     white label at 4.51:1; --accent #237C5B measures 5.11:1 on white / 4.66:1 on
     the field grey. `--mint` is the canonical name for the accent mint
     (docs/Reference/Styling/tokens.css); `--brand-accent` stays as the shipped
     alias until per-surface migration retires it. */
  '--deep': '#0E8761',
  '--accent': '#237C5B',
  '--brand-accent': '#05FABD',
  '--mint': '#05FABD',

  /* Risk spectrum — fills. `--warn-fill` / `--fail-fill` are the canonical names
     (Styling canon TOK-01); `--risk-warn` / `--risk-bad` stay as shipped aliases
     during per-surface migration. `--risk-pass` is canonical as-is. */
  '--risk-pass': '#00BE85',
  '--risk-ok': '#89CF55',
  '--risk-warn': '#E1A700',
  '--warn-fill': '#E1A700',
  '--risk-caution': '#E3E300',
  '--risk-bad': '#E44F23',
  '--fail-fill': '#E44F23',
  '--info': '#264FC4',

  /* Verdict text — the canonical trio (Styling canon TOK-02/TOK-04) and their
     shipped aliases, now emitting the CANON values: pass text is #0B6B4C, not the
     verb green (the #0E8A63 double-duty was the confusion TOK-02 exists to end). */
  '--pass-text': '#0B6B4C',
  '--warn-text': '#7A5B00',
  '--fail-text': '#C4390F',
  '--risk-pass-text': '#0B6B4C',
  '--risk-warn-text': '#7A5B00',
  '--risk-bad-text': '#C4390F',

  /* Ink ON a chromatic fill — never text on a light ground. A label on a fill wears
     the fill's named ink: --on-fail (5.09:1), never white (3.86:1) — PR-4 / TOK-04. */
  '--on-deep': '#FFFFFF',
  '--on-mint': '#08110D',
  '--on-warn': '#231A00',
  '--on-fail': '#1A0600',

  /* Status tints — mixed on --surface, never literal white (dark-safe by construction) */
  '--tint-pass': 'color-mix(in srgb, var(--risk-pass) 14%, var(--surface))',
  '--tint-warn': 'color-mix(in srgb, var(--risk-warn) 18%, var(--surface))',
  '--tint-bad': 'color-mix(in srgb, var(--risk-bad) 14%, var(--surface))',

  /* Skeleton — the loading state's two stops (§14.2 / §14.9): `a` is the resting bar,
     `b` the shimmer sheen that travels across it. A pair rather than one value because
     the shimmer IS the only motion the state carries, and `prefers-reduced-motion`
     drops it back to the flat `--skel-a`. */
  '--skel-a': '#EAEEEC',
  '--skel-b': '#F5F8F6',

  /* Categorical chart order — one-hue rule for same-thing comparisons */
  '--chart-1': '#00BE85',
  '--chart-2': '#33267D',
  '--chart-3': '#05DEBD',
  '--chart-4': '#264FC4',
  '--chart-5': '#00908B',
  '--chart-6': '#90278E',

  /* Elevation + radius — cards are borderless, soft elevation. `--shadow` is the
     canonical name (Styling canon); `--elev` stays as the shipped alias. */
  '--elev': '0 1px 2px rgba(12,15,14,.04), 0 6px 22px rgba(12,15,14,.06)',
  '--shadow': '0 1px 2px rgba(12,15,14,.04), 0 6px 22px rgba(12,15,14,.06)',
  '--r': '12px',

  /* Typography */
  '--font-ui': FONT_UI,
  '--font-display': FONT_DISPLAY,
};

/**
 * 4:2:1 brand token catalogue — dark. A re-expression, not an inversion: elevation is
 * surface steps + hairlines (shadows barely read), mint becomes the accent, and status
 * text switches to the dark-safe set. `--shell` is deliberately NOT overridden.
 */
export const brandTokensDark: Readonly<Record<string, string>> = {
  ...brandTokensLight,

  /* Surfaces — elevation as steps: bg → surface → raised */
  '--bg': '#0E1210',
  '--surface': '#161C19',
  '--raised': '#1F2622',
  '--line': '#28302C',
  '--hairline': '#28302C',
  '--line-soft': '#1F2622',

  /* Text */
  '--text': '#E9EFEC',
  '--muted': '#96A39C',

  /* Mint is the dark accent AND the primary-verb fill (ink text) */
  '--accent': '#05FABD',

  /* Dark-safe status text — canonical trio + shipped aliases, same values */
  '--pass-text': '#3BD9A0',
  '--warn-text': '#EBC35B',
  '--fail-text': '#FF8A66',
  '--risk-pass-text': '#3BD9A0',
  '--risk-bad-text': '#FF8A66',
  '--risk-warn-text': '#EBC35B',

  /* Skeleton — SURFACE STEPS, never a lightened white wash (§14.9, dark rule 1). These are
     exactly `--line-soft` → `--line`, so a skeleton in dark reads as the same elevation
     language as every hairline on the page rather than as a grey film laid over it. */
  '--skel-a': '#1F2622',
  '--skel-b': '#28302C',

  '--elev': '0 1px 0 rgba(255,255,255,.03), 0 10px 30px rgba(0,0,0,.4)',
  '--shadow': '0 1px 0 rgba(255,255,255,.03), 0 10px 30px rgba(0,0,0,.4)',
};

/** The brand token catalogue for a resolved theme. */
export function brandTokens(isDark: boolean): Readonly<Record<string, string>> {
  return isDark ? brandTokensDark : brandTokensLight;
}

/** GMS-branded Fluent light theme (stock neutral ramp + the GMS brand ramp). */
export const gmsLightTheme: Theme = {
  ...createLightTheme(gmsBrand),
  fontFamilyBase: FONT_UI,
  fontFamilyNumeric: FONT_DISPLAY,
};

/**
 * GMS-branded Fluent dark theme.
 *
 * `createDarkTheme` derives the brand aliases from the ramp, which puts deep green
 * (`#0E7A59` fill, `#4BB890` foreground) on every primary verb, link and active state
 * in dark — the exact opposite of dark rule 2, which makes **mint the dark accent AND
 * the primary-verb fill, with ink text** (design language §12; see the ratified dark
 * shots 02-ribbon / 09-executive / 12-attestations / 16-cost-management). Overriding
 * the brand aliases here rather than per-surface is what makes that rule INHERITED:
 * every Fluent primary Button, brand Badge, link and selected state flips at once, with
 * no `[data-theme]` block anywhere in CSS (#5870).
 *
 * `colorNeutralForegroundOnBrand` becomes shell ink — it means "text on the BRAND
 * fill", and the brand fill is now mint. Filled swatches that are red/blue/etc. take
 * `colorNeutralForegroundStaticInverted` (white in both themes) instead, which is what
 * Fluent's own non-brand filled Badge already uses.
 */
export const gmsDarkTheme: Theme = {
  ...createDarkTheme(gmsBrand),

  /* Rule 2 — mint is the dark accent and the primary-verb fill, ink text on it. */
  colorBrandBackground: '#05FABD',
  colorBrandBackgroundHover: '#3BD9A0',
  colorBrandBackgroundPressed: '#2AA379',
  colorNeutralForegroundOnBrand: '#0C0F0E',

  /* Brand-tinted surfaces step UP from the surface, never down (rule 1): the derived
     #062E22 is DARKER than `--surface`, so a "selected" row read as a hole. This is
     mint at 18% over #161C19. */
  colorBrandBackground2: '#134437',

  /* Links, active states, focus strokes — mint, not the ramp's muted green. */
  colorBrandForeground1: '#05FABD',
  colorBrandForeground2: '#3BD9A0',
  colorCompoundBrandForeground1: '#05FABD',
  colorCompoundBrandForeground1Hover: '#3BD9A0',
  colorCompoundBrandForeground1Pressed: '#2AA379',
  colorBrandStroke1: '#05FABD',
  colorCompoundBrandStroke: '#05FABD',

  fontFamilyBase: FONT_UI,
  fontFamilyNumeric: FONT_DISPLAY,
};
